import '../styles/globals.css';
import '@fontsource/lexend/300.css';
import '@fontsource/lexend/400.css';
import '@fontsource/lexend/500.css';
import '@fontsource/lexend/600.css';

import { start } from '@portalterreno/nextjs-core/bugsnag';
import DeviceProvider from '@portalterreno/react-ui/DeviceProvider';
import Global from '@portalterreno/react-ui-core/Global';
import { ThemeProvider } from '@portalterreno/react-ui-themes';
import type { NextPage } from 'next';
import App, { AppContext, AppProps } from 'next/app';
import Script from 'next/script';
import { appWithTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import React from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import Moment from 'react-moment';
import { Provider } from 'react-redux';

import MainLayout from '../components/MainLayout';
import fetchDeviceInfo from '../lib/fetchDeviceInfo';
import { wrapper } from '../redux/store';

export type NextPageWithLayout = NextPage & {
  layout?: React.ComponentType | false;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

Moment.globalLocale = process.env.NEXT_PUBLIC_LANGUAGE;

start();

function PortalTerrenoApp({
  Component,
  pageProps: { isMobile, isDesktop, ...pageProps },
  ...rest
}: AppPropsWithLayout) {
  const LayoutComponent: React.ComponentType<React.PropsWithChildren> =
    React.useMemo(() => {
      if (Component.layout === false) {
        return React.Fragment;
      }

      if (Component.layout) {
        return Component.layout;
      }

      return MainLayout;
    }, [Component.layout]);

  const { store, props } = wrapper.useWrappedStore({
    pageProps,
    ...rest
  });

  return (
    <React.Fragment>
      {process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID && (
        <Script
          id='google-tag-manager'
          strategy='afterInteractive'
        >
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl+
          '&gtm_auth=${process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_AUTH}&gtm_preview=${process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_PREVIEW}&gtm_cookies_win=x';
          f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID}');`}
        </Script>
      )}
      <Provider store={store}>
        <ThemeProvider>
          <GoogleReCaptchaProvider
            reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
          >
            <DeviceProvider
              isMobile={isMobile}
              isDesktop={isDesktop}
            >
              <Global />
              <LayoutComponent>
                <Component {...props.pageProps} />
              </LayoutComponent>
            </DeviceProvider>
          </GoogleReCaptchaProvider>
        </ThemeProvider>
      </Provider>
    </React.Fragment>
  );
}

PortalTerrenoApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);

  const deviceInfo = fetchDeviceInfo(appContext.ctx);

  const serverSideTranslationsProps = await serverSideTranslations(
    process.env.NEXT_PUBLIC_LANGUAGE,
    ['common', 'property', 'social', 'onboarding']
  );

  appProps.pageProps = {
    ...serverSideTranslationsProps,
    ...appProps.pageProps,
    ...deviceInfo
  };

  return appProps;
};

export default appWithTranslation(PortalTerrenoApp);
